import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Modal } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";

import Pagination from "../components/Pagination";
import { generateOverallLedgerPdfBlob } from "../utils/generateOverallLedgerPdf";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  postOverallLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";

function processOverallLedgerEntries(entries) {
  return entries.map((entry) => {
    // Format date from YYYY-MM-DD to DD/MM/YYYY
    let formattedDate =
      entry.date ||
      entry.voucher_date ||
      entry.invoice_date ||
      entry.purchase_date ||
      "-";
    if (formattedDate !== "-" && formattedDate.includes("-")) {
      const dateParts = formattedDate.split(" ")[0].split("-");
      if (dateParts.length === 3) {
        formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      }
    }

    // Determine voucher type
    const voucherType =
      entry.voucher_type ||
      entry.type ||
      (entry.invoice_no ? "SALE" : entry.purchase_no ? "PURCHASE" : "-");

    // Format name:
    // For PURCHASE: only company_name
    // For SALE: company_name (name) if name exists, otherwise just company_name
    let displayName = "-";
    const voucherTypeUpper = (voucherType || "").toUpperCase();
    if (voucherTypeUpper === "PURCHASE") {
      displayName = entry.company_name || entry.name || "-";
    } else {
      if (entry.name && entry.name.trim() && entry.company_name) {
        displayName = `${entry.company_name} `;
      } else if (entry.company_name) {
        displayName = entry.company_name;
      } else if (entry.name) {
        displayName = entry.name;
      }
    }

    // Get voucher number
    const voucherNo =
      entry.voucher_number || entry.invoice_no || entry.purchase_no || "-";

    // Get voucher value (total_amount for purchase, grand_total for sale)
    const voucherValue = parseFloat(
      entry.total_amount || entry.grand_total || entry.voucher_value || 0
    );

    // For purchase entries, store values as negative for calculation
    // For sale entries, store values as positive
    const isPurchase = voucherTypeUpper === "PURCHASE";
    const multiplier = isPurchase ? -1 : 1;

    const processedVoucherValue = voucherValue * multiplier;
    const processedTaxableValue =
      parseFloat(
        entry.gross_amount || entry.final_amount || entry.taxable_value || 0
      ) * multiplier;

    return {
      voucher_type: voucherType,
      name: displayName,
      phone: entry.contact_number || entry.phone || entry.mobile || "-",
      voucher_no: voucherNo,
      voucher_date: formattedDate,
      voucher_value: processedVoucherValue,
      taxable_value: processedTaxableValue,
      sgst: parseFloat(entry.sgst || 0) * multiplier,
      cgst: parseFloat(entry.cgst || 0) * multiplier,
      total_tax:
        parseFloat(entry.gst_amount || entry.total_tax || entry.gst || 0) *
        multiplier,
      gold_gm: parseFloat(entry.gold_gm || entry.gold_weight || 0) * multiplier,
      silver_gm:
        parseFloat(entry.silver_gm || entry.silver_weight || 0) * multiplier,
    };
  });
}

function summarizeOverallLedgerRows(processedEntries) {
  return processedEntries.reduce(
    (acc, entry) => {
      acc.voucher_value += entry.voucher_value;
      acc.taxable_value += entry.taxable_value;
      acc.sgst += entry.sgst;
      acc.cgst += entry.cgst;
      acc.total_tax += entry.total_tax;
      acc.gold_gm += entry.gold_gm;
      acc.silver_gm += entry.silver_gm;
      return acc;
    },
    {
      voucher_value: 0,
      taxable_value: 0,
      sgst: 0,
      cgst: 0,
      total_tax: 0,
      gold_gm: 0,
      silver_gm: 0,
    }
  );
}

function OverallLedger() {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfUrlRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);

  const pageCacheRef = useRef(new Map());

  // Clean up blob URL on component unmount
  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);

  const fetchOverallLedger = useCallback(
    async ({ page = 1, limit = 10, silent = false, bypassCache = false } = {}) => {
      const cacheKey = entireReport ? `${page}:${limit}` : "";
      if (cacheKey && !bypassCache) {
        const cached = pageCacheRef.current.get(cacheKey);
        if (cached) {
          setLedgerRows(cached.ledgerRows);
          setSummaryData(cached.summaryData);
          setCurrentPage(cached.currentPage);
          setTotalCount(cached.totalCount);
          setHasMorePages(cached.hasMorePages);
          setLoading(false);
          return;
        }
      }

      setLoading(true);

      const apiPayload = {};

      if (entireReport) {
        apiPayload.type = "entire";
        apiPayload.page = page;
        apiPayload.limit = limit;
      } else {
        if (fromDate) apiPayload.from_date = fromDate;
        if (toDate) apiPayload.to_date = toDate;
      }

      try {
        const payload = await postOverallLedger(apiPayload);

        if (!payload || (!isErpApiSuccess(payload) && payload.message !== "radha")) {
          toast.error(
            payload?.message || "Failed to fetch ledger data. Please try again."
          );
          setLedgerRows([]);
          setSummaryData(null);
          return;
        }

        const entries = payload.data?.entries || payload.data || [];

        if (!Array.isArray(entries) || entries.length === 0) {
          toast.error("No ledger entries found for the selected date range.");
          setLedgerRows([]);
          setSummaryData(null);
          return;
        }

        const processedEntries = processOverallLedgerEntries(entries);
        const summary = summarizeOverallLedgerRows(processedEntries);

        setLedgerRows(processedEntries);
        setSummaryData(summary);

        const paginationMeta = entireReport
          ? parseApiPagination(payload, processedEntries.length)
          : null;

        if (entireReport && paginationMeta) {
          setCurrentPage(paginationMeta.page);
          setTotalCount(paginationMeta.total);
          setHasMorePages(paginationMeta.hasMore);
          if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
        } else {
          setCurrentPage(1);
          setTotalCount(processedEntries.length);
          setHasMorePages(false);
        }

        if (cacheKey) {
          pageCacheRef.current.set(cacheKey, {
            ledgerRows: processedEntries,
            summaryData: summary,
            currentPage: paginationMeta?.page || page,
            totalCount: paginationMeta?.total ?? processedEntries.length,
            hasMorePages: Boolean(paginationMeta?.hasMore),
          });
        }

        if (!silent) toast.success("Ledger generated successfully!");
      } catch (error) {
        toast.error(
          getErpRequestErrorMessage(
            error,
            "An error occurred while fetching the ledger data."
          )
        );
        setLedgerRows([]);
        setSummaryData(null);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [entireReport, fromDate, toDate]
  );

  const fetchRef = useRef(fetchOverallLedger);
  fetchRef.current = fetchOverallLedger;

  const initialFetchRef = useRef(false);
  const ignoreNextPageFetchRef = useRef(false);
  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
    fetchRef.current({ page: 1, limit: itemsPerPage });
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log("API URL changed in OverallLedger, refetching ledger...", event.detail);
      pageCacheRef.current.clear();
      fetchRef.current({
        page: currentPage,
        limit: itemsPerPage,
        silent: true,
        bypassCache: true,
      });
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [currentPage, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    pageCacheRef.current.clear();
    setCurrentPage(1);
    await fetchOverallLedger({
      page: 1,
      limit: itemsPerPage,
      bypassCache: true,
    });
  };

  const handlePageChange = (page) => {
    if (ignoreNextPageFetchRef.current) {
      ignoreNextPageFetchRef.current = false;
      return;
    }
    setCurrentPage(page);
    fetchOverallLedger({ page, limit: itemsPerPage, silent: true });
  };

  const handleItemsPerPageChange = (limit) => {
    ignoreNextPageFetchRef.current = true;
    pageCacheRef.current.clear();
    setItemsPerPage(limit);
    setCurrentPage(1);
    fetchOverallLedger({ page: 1, limit, silent: true, bypassCache: true });
  };

  const handlePrint = async () => {
    if (!ledgerRows.length && totalCount === 0) {
      toast.error("Please generate the ledger first.");
      return;
    }
    if (printLoading) return;

    setPrintLoading(true);

    const printPayload = {
      page: 0,
      limit: 0,
    };

    if (entireReport) {
      printPayload.type = "entire";
      printPayload.filter_type = "entire";
    } else {
      if (fromDate) printPayload.from_date = fromDate;
      if (toDate) printPayload.to_date = toDate;
    }

    try {
      const payload = await postOverallLedger(printPayload);

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        toast.error(
          payload?.message || "Failed to fetch ledger data for printing. Please try again."
        );
        return;
      }

      const entries = payload.data?.entries || payload.data || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found to print.");
        return;
      }

      const processedEntries = processOverallLedgerEntries(entries);
      const summary = summarizeOverallLedgerRows(processedEntries);

      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }

      const blob = await generateOverallLedgerPdfBlob({
        ledgerData: processedEntries,
        summaryData: summary,
      });

      const blobUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = blobUrl;
      setPdfBlobUrl(blobUrl);
      setShowPDFModal(true);
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while generating the print data."
        )
      );
      console.error("Error generating overall ledger for print:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Format currency with ₹ symbol and commas
  const formatCurrency = (value) => {
    if (value === 0) return "-";
    const absValue = Math.abs(value);
    return `₹ ${Math.round(absValue).toLocaleString("en-IN")}`;
  };

  // Format currency with decimals in Indian format
  const formatCurrencyDecimal = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return "-";
    const absValue = Math.abs(n);
    return `₹ ${absValue.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format weight with 3 decimal places
  const formatWeight = (value) => {
    if (value === 0) return "-";
    const absValue = Math.abs(value);
    return absValue.toFixed(3);
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Overall Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* From Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Overall Ledger
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                    disabled={printLoading || loading}
                  >
                    {printLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Fetching records...
                      </>
                    ) : (
                      "Print Report"
                    )}
                  </button>
                </div>
                <div className={`ledger-report-wrap mb-4 ${cardClass}`}>
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle ledger-report-table">
                      <thead>
                        <tr>
                          <th rowSpan="2" className="text-nowrap">VOUCHER TYPE</th>
                          <th colSpan="2" className="text-center">BUYER/SUPPLIER DETAILS</th>
                          <th colSpan="3" className="text-center">VOUCHER DETAILS</th>
                          <th rowSpan="2" className="text-end text-nowrap">TAXABLE VALUE</th>
                          <th colSpan="3" className="text-center">AMOUNT OF TAX</th>
                          <th colSpan="2" className="text-center">WEIGHT</th>
                        </tr>
                        <tr>
                          <th className="text-nowrap">NAME</th>
                          <th className="text-nowrap">Phone</th>
                          <th className="text-nowrap">VOUCHER NO</th>
                          <th className="text-nowrap">VOUCHER DATE</th>
                          <th className="text-end text-nowrap">VOUCHER VALUE</th>
                          <th className="text-end text-nowrap">SGST</th>
                          <th className="text-end text-nowrap">CGST</th>
                          <th className="text-end text-nowrap">TOTAL TAX</th>
                          <th className="text-end text-nowrap">GOLD (GM)</th>
                          <th className="text-end text-nowrap">SILVER (GM)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerRows.map((row, idx) => {
                          return (
                            <tr key={idx}>
                              <td>{row.voucher_type}</td>
                              <td>{row.name}</td>
                              <td>{row.phone}</td>
                              <td>{row.voucher_no}</td>
                              <td>{row.voucher_date}</td>
                              <td className="text-end">
                                {formatCurrency(row.voucher_value)}
                              </td>
                              <td className="text-end">
                                {formatCurrencyDecimal(row.taxable_value)}
                              </td>
                              <td className="text-end">
                                {formatCurrencyDecimal(row.sgst)}
                              </td>
                              <td className="text-end">
                                {formatCurrencyDecimal(row.cgst)}
                              </td>
                              <td className="text-end">
                                {formatCurrencyDecimal(row.total_tax)}
                              </td>
                              <td className="text-end">
                                {row.gold_gm !== 0 ? formatWeight(row.gold_gm) : "-"}
                              </td>
                              <td className="text-end">
                                {row.silver_gm !== 0 ? formatWeight(row.silver_gm) : "-"}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Summary Row */}
                        {summaryData && (
                          <tr className="ledger-report-summary">
                            <td colSpan="5"></td>
                            <td className="text-end">
                              {formatCurrency(summaryData.voucher_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.taxable_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.sgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.cgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.total_tax)}
                            </td>
                            <td className="text-end">
                              {summaryData.gold_gm !== 0 ? formatWeight(summaryData.gold_gm) : "-"}
                            </td>
                            <td className="text-end">
                              {summaryData.silver_gm !== 0 ? formatWeight(summaryData.silver_gm) : "-"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {entireReport && totalCount > 0 && (
              <Pagination
                totalItems={totalCount}
                hasMore={hasMorePages}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>

      {/* Print Loading Modal */}
      <Modal
        show={printLoading}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="mb-2">Preparing Overall Ledger PDF</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
            This may take a few minutes. Thank you for your patience and understanding.
          </p>
        </Modal.Body>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
          }
          setPdfBlobUrl(null);
        }}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Overall Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", padding: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              title="Overall Ledger PDF Preview"
              src={pdfBlobUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading PDF...</span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default OverallLedger;
